import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
    ResponsiveContainer, CartesianGrid
} from "recharts";
import AdminLayout, { AdminPageHead } from "./AdminLayout";
import { request } from "../service/HttpService";
import { isRateLimited } from "../service/HttpErrors";
import { getJwtRole } from "../service/AuthService";
import { admin } from "../constants/Constants";

function barChartHeight(rows) {
    return Math.max(240, rows * 26 + 30);
}

function formatDate(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTimestamp(ts) {
    if (!ts) return "";
    return new Date(ts).toLocaleString("en-US", {
        month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
}

// Stacked sign-up series: the busiest sources get their own colour, the rest
// fold into "other" so the chart stays readable as new sources appear.
const SERIES_COLORS = ["var(--series-blue)", "var(--series-amber)", "var(--series-green)", "var(--series-brand)"];

function SourceChip({ source }) {
    return (
        <span style={{
            fontSize: 11, padding: "2px 8px", borderRadius: 5,
            background: "var(--blue-bg)", color: "var(--blue)", fontWeight: 500,
        }}>
            {source}
        </span>
    );
}

function last30UtcDays() {
    const now = new Date();
    const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return Array.from({ length: 30 }, (_, i) => new Date(todayUtc - (29 - i) * 86400000).toISOString().slice(0, 10));
}

function ChartCard({ title, subtitle, children }) {
    return (
        <div className="submissions-card" style={{ padding: "20px" }}>
            <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--ink-900)" }}>{title}</p>
            {subtitle && <p style={{ margin: 0, fontSize: 12, color: "var(--ink-300)", marginTop: 2 }}>{subtitle}</p>}
            <div style={{ marginTop: 16 }}>{children}</div>
        </div>
    );
}

export default function AdminTraffic() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (getJwtRole() !== admin) { navigate("/"); return; }
        request("GET", "/admin/traffic")
            .then(res => { setData(res); setLoading(false); })
            .catch(err => isRateLimited(err) ? setLoadError(err.message) : navigate("/login"));
    }, []);

    if (loading) {
        return (
            <AdminLayout>
                        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>{loadError || "Loading traffic..."}</div>
            </AdminLayout>
        );
    }

    const bySource = (data.visitsBySource ?? []).map(s => ({
        name: s.source ?? "unknown",
        count: s.count,
    }));

    // Visits and sign-ups side by side, per source and per campaign
    const mergeCounts = (visits, signups, keyOf) => {
        const rows = new Map();
        visits.forEach(v => rows.set(keyOf(v), { ...v, visits: v.count, signups: 0 }));
        signups.forEach(s => {
            const row = rows.get(keyOf(s));
            if (row) row.signups = s.count;
            else rows.set(keyOf(s), { ...s, visits: 0, signups: s.count });
        });
        return [...rows.values()].sort((a, b) => b.signups - a.signups || b.visits - a.visits);
    };
    const byCampaign = mergeCounts(data.visitsByCampaign ?? [], data.signupsByCampaign ?? [], r => `${r.source}\u0000${r.campaign}`);
    const sourceFunnel = mergeCounts(data.visitsBySource ?? [], data.signupsBySource ?? [], r => r.source);

    const signupsBySource = (data.signupsBySource ?? []).map(s => ({ name: s.source ?? "unknown", count: s.count }));
    // Series use generated keys (s0..s3, rest) so a source literally named
    // "label" or "other" can't collide with the axis key or the fold-in bucket
    const topSignupSources = signupsBySource.slice(0, SERIES_COLORS.length).map(s => s.name);
    const hasOtherSources = signupsBySource.length > SERIES_COLORS.length;
    const seriesKey = source => {
        const i = topSignupSources.indexOf(source);
        return i >= 0 ? `s${i}` : "rest";
    };
    const signupsByDay = (() => {
        const byDate = new Map();
        (data.signupsByDay ?? []).forEach(({ date, source, count }) => {
            const key = seriesKey(source);
            const day = byDate.get(date) ?? {};
            day[key] = (day[key] ?? 0) + count;
            byDate.set(date, day);
        });
        return last30UtcDays().map(iso => {
            const day = byDate.get(iso) ?? {};
            const row = { label: formatDate(iso) };
            topSignupSources.forEach((_, i) => { row[`s${i}`] = day[`s${i}`] ?? 0; });
            if (hasOtherSources) row.rest = day.rest ?? 0;
            return row;
        });
    })();
    const recentSignups = data.recentSignups ?? [];

    // Backfill 30 days so the chart always has a continuous range. Axis days
    // are UTC to match the backend's UTC bucketing — a local-time axis would
    // silently drop this evening's visits (bucketed under UTC "tomorrow").
    const visitsByDay = (() => {
        const countsByDate = new Map((data.visitsByDay ?? []).map(d => [d.date, d.count]));
        const days = [];
        const now = new Date();
        const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        for (let i = 29; i >= 0; i--) {
            const iso = new Date(todayUtc - i * 86400000).toISOString().slice(0, 10);
            days.push({
                date: iso,
                count: countsByDate.get(iso) ?? 0,
                label: formatDate(iso),
            });
        }
        return days;
    })();

    const recent = data.recentVisits ?? [];

    return (
        <AdminLayout>

                    <AdminPageHead
                        title="Traffic Sources"
                        subtitle={`${data.totalVisits ?? 0} ad-landing visits (tagged via ?source= on /tutorial) · ${data.totalSignups ?? 0} tracked sign-ups`}
                    />

                    <div className="analytics-charts-row">
                        <ChartCard title="Visits by Source" subtitle="Where landing-page traffic comes from">
                            {bySource.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No visits tracked yet</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={barChartHeight(bySource.length)}>
                                    <BarChart data={bySource} layout="vertical" margin={{ top: 4, right: 12, left: 10, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" horizontal={false} />
                                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-500)" }} axisLine={false} tickLine={false} width={120} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="count" fill="var(--accent)" radius={[0, 4, 4, 0]} name="Visits" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard title="Visits Over Time" subtitle="Landing-page visits per day (last 30 days)">
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={visitsByDay} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                    <Line type="monotone" dataKey="count" stroke="var(--blue)" strokeWidth={2} dot={false} name="Visits" />
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <div className="analytics-charts-row">
                        <ChartCard
                            title="Sign-ups by Source"
                            subtitle={`${data.totalSignups ?? 0} tracked sign-ups · ${data.untrackedAccounts ?? 0} accounts from before tracking not shown`}
                        >
                            {signupsBySource.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No sign-ups tracked yet</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={barChartHeight(signupsBySource.length)}>
                                    <BarChart data={signupsBySource} layout="vertical" margin={{ top: 4, right: 12, left: 10, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" horizontal={false} />
                                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-500)" }} axisLine={false} tickLine={false} width={120} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="count" fill="var(--series-brand)" radius={[0, 4, 4, 0]} name="Sign-ups" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard title="Sign-ups Over Time" subtitle="New accounts per day by source (last 30 days, UTC)">
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={signupsByDay} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    {topSignupSources.map((source, i) => (
                                        <Bar key={`s${i}`} dataKey={`s${i}`} stackId="signups" fill={SERIES_COLORS[i]} name={source} />
                                    ))}
                                    {hasOtherSources && <Bar dataKey="rest" stackId="signups" fill="var(--chart-muted)" name="other sources" />}
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <div className="analytics-charts-row">
                        <ChartCard title="Visits vs Sign-ups" subtitle="Per source. Visits are /tutorial landings; sign-ups count every new account">
                            {sourceFunnel.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No traffic tracked yet</p>
                            ) : (
                                <table className="submissions-table" style={{ fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th>Source</th>
                                            <th style={{ textAlign: "right" }}>Visits</th>
                                            <th style={{ textAlign: "right" }}>Sign-ups</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sourceFunnel.map(r => (
                                            <tr key={r.source}>
                                                <td><SourceChip source={r.source} /></td>
                                                <td style={{ textAlign: "right" }}>{r.visits}</td>
                                                <td style={{ textAlign: "right", fontWeight: 600 }}>{r.signups}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </ChartCard>

                        <ChartCard title="Recent Sign-ups" subtitle="Last 50 new accounts and where they came from">
                            {recentSignups.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No sign-ups tracked yet</p>
                            ) : (
                                <div style={{ maxHeight: 320, overflowY: "auto", overflowX: "auto" }}>
                                    <table className="submissions-table" style={{ fontSize: 12 }}>
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Source</th>
                                                <th>Campaign</th>
                                                <th>When</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentSignups.map((s, i) => (
                                                <tr
                                                    key={i}
                                                    className={s.userId ? "submission-row" : undefined}
                                                    style={{ cursor: s.userId ? "pointer" : "default" }}
                                                    onClick={() => s.userId && navigate(`/admin/user?id=${s.userId}`)}
                                                >
                                                    <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {s.userId
                                                            ? <span title={s.email}>{s.name || s.email}</span>
                                                            : <span style={{ color: "var(--ink-300)" }}>Deleted account</span>}
                                                        {s.method === "google" && <span style={{ color: "var(--ink-300)" }}> · Google</span>}
                                                    </td>
                                                    <td><SourceChip source={s.source} /></td>
                                                    <td style={{ color: "var(--ink-500)" }}>{s.campaign || "—"}</td>
                                                    <td style={{ color: "var(--ink-350)", whiteSpace: "nowrap" }}>{formatTimestamp(s.createdAt)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </ChartCard>
                    </div>

                    <div className="analytics-charts-row">
                        <ChartCard title="Campaigns" subtitle="Visits and sign-ups by source and campaign (utm_campaign)">
                            {byCampaign.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No campaign-tagged visits or sign-ups yet</p>
                            ) : (
                                <table className="submissions-table" style={{ fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th>Source</th>
                                            <th>Campaign</th>
                                            <th style={{ textAlign: "right" }}>Visits</th>
                                            <th style={{ textAlign: "right" }}>Sign-ups</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {byCampaign.map((c, i) => (
                                            <tr key={i}>
                                                <td style={{ fontWeight: 500 }}>{c.source}</td>
                                                <td style={{ color: "var(--ink-500)" }}>{c.campaign}</td>
                                                <td style={{ textAlign: "right" }}>{c.visits}</td>
                                                <td style={{ textAlign: "right" }}>{c.signups}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </ChartCard>

                        <ChartCard title="Recent Visits" subtitle="Last 50 tracked landing-page visits">
                            {recent.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No visits tracked yet</p>
                            ) : (
                                <div style={{ maxHeight: 320, overflowY: "auto", overflowX: "auto" }}>
                                    <table className="submissions-table" style={{ fontSize: 12 }}>
                                        <thead>
                                            <tr>
                                                <th>Source</th>
                                                <th>Campaign</th>
                                                <th>Referrer</th>
                                                <th>When</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recent.map((r, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <span style={{
                                                            fontSize: 11, padding: "2px 8px", borderRadius: 5,
                                                            background: "var(--blue-bg)", color: "var(--blue)", fontWeight: 500,
                                                        }}>
                                                            {r.source}
                                                        </span>
                                                    </td>
                                                    <td style={{ color: "var(--ink-500)" }}>{r.campaign || "—"}</td>
                                                    <td style={{ color: "var(--ink-350)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.referrer || "—"}</td>
                                                    <td style={{ color: "var(--ink-350)", whiteSpace: "nowrap" }}>{formatTimestamp(r.timestamp)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </ChartCard>
                    </div>

        </AdminLayout>
    );
}
